import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

const STATUS_FILTERS = [
  { value: 'all', label: 'Tutti' },
  { value: 'active', label: 'Attivi' },
  { value: 'acknowledged', label: 'Presi in carico' },
  { value: 'resolved', label: 'Risolti' },
];

const VOICE_FILTERS = [
  { value: 'all', label: 'Tutte chiamate' },
  { value: 'none', label: 'Non avviate' },
  { value: 'scheduled', label: 'Schedulate' },
  { value: 'in_progress', label: 'In corso' },
  { value: 'acknowledged', label: 'ACK telefonico' },
  { value: 'problems', label: 'Problemi' },
];

const EVENTS_REFRESH_MS = 15000;

function formatDateTime(value) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return '-';
  }
}

function formatValue(value, fallback = '-') {
  if (value === null || value === undefined || value === '') return fallback;

  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  return n.toFixed(2);
}

function getRefLabel(ref, fallback = '-') {
  if (!ref) return fallback;

  if (typeof ref === 'string') return ref;

  return (
    ref.name ||
    ref.nome ||
    ref.username ||
    ref.matricola ||
    fallback
  );
}

function getCustomerLabel(customer) {
  if (!customer) return '-';

  if (typeof customer === 'string') return customer;

  return (
    `${customer.nome || ''} ${customer.cognome || ''}`.trim() ||
    customer.username ||
    customer.matricola ||
    '-'
  );
}

function getRuleStateBadge(rule) {
  const state = rule?.current_state || 'normal';

  if (state === 'alarm') {
    return {
      label: 'In allarme',
      className: 'bg-red-100 text-red-700 border-red-200',
    };
  }

  if (state === 'pending') {
    return {
      label: 'Fuori soglia',
      className: 'bg-amber-100 text-amber-700 border-amber-200',
    };
  }

  return {
    label: 'Normale',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
}

function getEventBadge(status) {
  if (status === 'active') {
    return {
      label: 'Attivo',
      className: 'bg-red-100 text-red-700 border-red-200',
    };
  }

  if (status === 'acknowledged') {
    return {
      label: 'Preso in carico',
      className: 'bg-amber-100 text-amber-700 border-amber-200',
    };
  }

  if (status === 'resolved') {
    return {
      label: 'Risolto',
      className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
  }

  return {
    label: status || '-',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  };
}

function getEventTypeLabel(type) {
  if (type === 'low_temperature') return 'Temperatura bassa';
  if (type === 'high_temperature') return 'Temperatura alta';
  return type || 'Allarme';
}

function getVoiceStatusLabel(status) {
  const normalized = normalizeVoiceStatus(status);

  const labels = {
    scheduled: 'Schedulata',
    pending: 'In attesa',
    initiated: 'Avviata',
    ringing: 'Sta squillando',
    answered: 'Risposta',
    completed: 'Completata',
    acknowledged: 'Confermata',
    no_answer: 'Nessuna risposta',
    busy: 'Occupato',
    failed: 'Errore',
    canceled: 'Annullata',
    skipped: 'Saltata',
    escalation_failed: 'Escalation fallita',
    invalid_digit: 'Tasto non valido',
    rate_limited: 'Anti-spam',
    unknown: 'Sconosciuto',
  };

  return labels[normalized] || status || '-';
}

function normalizeVoiceStatus(status = '') {
  return String(status || '').trim().toLowerCase().replace(/-/g, '_');
}

function getVoiceBadge(event = {}) {
  if (event.voice_acknowledged === true) {
    return {
      label: 'ACK telefonico',
      className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
  }
  const escalationStatus = normalizeVoiceStatus(event.voice_escalation_status);

  if (escalationStatus.includes('rate_limit')) {
    return {
      label: 'Anti-spam',
      className: 'bg-orange-100 text-orange-700 border-orange-200',
    };
  }
  const status = normalizeVoiceStatus(event.voice_call_status);

  if (!status) {
    return {
      label: 'Non avviata',
      className: 'bg-slate-100 text-slate-600 border-slate-200',
    };
  }

  if (status === 'scheduled' || status === 'pending') {
    return {
      label: 'Schedulata',
      className: 'bg-sky-100 text-sky-700 border-sky-200',
    };
  }

  if (status === 'initiated') {
    return {
      label: 'Avviata',
      className: 'bg-blue-100 text-blue-700 border-blue-200',
    };
  }

  if (status === 'ringing') {
    return {
      label: 'Sta squillando',
      className: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
  }

  if (status === 'answered') {
    return {
      label: 'Risposta',
      className: 'bg-purple-100 text-purple-700 border-purple-200',
    };
  }

  if (status === 'completed') {
    return {
      label: 'Terminata',
      className: 'bg-slate-100 text-slate-700 border-slate-200',
    };
  }

  if (status === 'busy') {
    return {
      label: 'Occupato',
      className: 'bg-amber-100 text-amber-700 border-amber-200',
    };
  }

  if (status === 'no_answer') {
    return {
      label: 'Nessuna risposta',
      className: 'bg-amber-100 text-amber-700 border-amber-200',
    };
  }

  if (status === 'failed' || status === 'canceled' || status === 'escalation_failed') {
    return {
      label: status === 'escalation_failed' ? 'Escalation fallita' : 'Errore chiamata',
      className: 'bg-red-100 text-red-700 border-red-200',
    };
  }

  if (status === 'skipped') {
    return {
      label: 'Saltata',
      className: 'bg-slate-100 text-slate-600 border-slate-200',
    };
  }

  if (status === 'invalid_digit') {
    return {
      label: 'Tasto non valido',
      className: 'bg-red-100 text-red-700 border-red-200',
    };
  }

  return {
    label: status,
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  };
}

function getVoiceAttemptCount(event = {}) {
  const n = Number(event.voice_call_attempts || 0);
  if (Number.isFinite(n) && n > 0) return n;

  if (Array.isArray(event.voice_attempts)) return event.voice_attempts.length;

  return 0;
}

function getVoiceLastAttempt(event = {}) {
  if (!Array.isArray(event.voice_attempts) || event.voice_attempts.length === 0) {
    return null;
  }

  return event.voice_attempts[event.voice_attempts.length - 1];
}


function getVoiceAttemptsTimeline(event = {}) {
  if (!Array.isArray(event.voice_attempts)) return [];

  return [...event.voice_attempts]
    .filter(Boolean)
    .reverse();
}

function isVoiceInProgress(event = {}) {
  const status = normalizeVoiceStatus(event.voice_call_status);

  return ['scheduled', 'pending', 'initiated', 'ringing', 'answered'].includes(status);
}

function canRetryVoiceCall(event = {}) {
  return (
    event.status === 'active' &&
    event.voice_acknowledged !== true &&
    !isVoiceInProgress(event)
  );
}

function canResetVoiceState(event = {}) {
  return (
    event.status !== 'resolved' &&
    (
      Boolean(event.voice_call_status) ||
      Number(event.voice_call_attempts || 0) > 0 ||
      Array.isArray(event.voice_attempts) && event.voice_attempts.length > 0 ||
      event.voice_acknowledged === true
    )
  );
}

function canManualAckVoice(event = {}) {
  return (
    event.status === 'active' &&
    event.voice_acknowledged !== true
  );
}


function eventMatchesVoiceFilter(event = {}, filter = 'all') {
  if (filter === 'all') return true;

  const status = normalizeVoiceStatus(event.voice_call_status);
  const escalationStatus = normalizeVoiceStatus(event.voice_escalation_status);
  const attempts = getVoiceAttemptCount(event);

  if (filter === 'none') {
    return !status && attempts === 0 && event.voice_acknowledged !== true;
  }

  if (filter === 'scheduled') {
    return ['scheduled', 'pending'].includes(status);
  }

  if (filter === 'in_progress') {
    return ['initiated', 'ringing', 'answered'].includes(status);
  }

  if (filter === 'acknowledged') {
    return event.voice_acknowledged === true || status === 'acknowledged';
  }

  if (filter === 'problems') {
    return (
      escalationStatus.includes('rate_limit') ||
      [
        'completed',
        'no_answer',
        'busy',
        'failed',
        'canceled',
        'skipped',
        'escalation_failed',
        'invalid_digit',
      ].includes(status)
    );
  }

  return true;
}

export default function AllarmiSoglie() {
  const navigate = useNavigate();

  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);

  const [loading, setLoading] = useState(true);
  const [savingRuleId, setSavingRuleId] = useState(null);
  const [actionEventId, setActionEventId] = useState(null);
  const [voiceActionEventId, setVoiceActionEventId] = useState(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [eventStatusFilter, setEventStatusFilter] = useState('all');
  const [voiceStatusFilter, setVoiceStatusFilter] = useState('all');
  const [query, setQuery] = useState('');

  const [forms, setForms] = useState({});

  const loadRules = async () => {
    const res = await authFetch('/api/alarms/rules');

    if (!res || !res.ok) {
      const data = await res?.json?.().catch(() => null);
      throw new Error(data?.message || 'Errore nel recupero delle soglie');
    }

    const data = await res.json();
    const list = Array.isArray(data?.rules) ? data.rules : [];

    setRules(list);

    setForms((prev) => {
      const next = { ...prev };

      for (const rule of list) {
        if (!rule?._id) continue;

        const alreadyEditing = savingRuleId === rule._id;

        if (alreadyEditing) {
          continue;
        }

        next[rule._id] = {
          min_value:
            rule.min_value === null || rule.min_value === undefined
              ? ''
              : String(rule.min_value),
          max_value:
            rule.max_value === null || rule.max_value === undefined
              ? ''
              : String(rule.max_value),
          delay_minutes:
            rule.delay_minutes === null || rule.delay_minutes === undefined
              ? '5'
              : String(rule.delay_minutes),
          enabled: Boolean(rule.enabled),
        };
      }

      return next;
    });
  };

  const loadEvents = async (status = eventStatusFilter) => {
    const res = await authFetch(
      `/api/alarms/events?status=${encodeURIComponent(status)}`
    );

    if (!res || !res.ok) {
      const data = await res?.json?.().catch(() => null);
      throw new Error(data?.message || 'Errore nel recupero degli allarmi');
    }

    const data = await res.json();
    setEvents(Array.isArray(data?.events) ? data.events : []);
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');

      await Promise.all([
        loadRules(),
        loadEvents(eventStatusFilter),
      ]);
    } catch (err) {
      setError(err?.message || 'Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEvents(eventStatusFilter).catch((err) => {
      setError(err?.message || 'Errore caricamento allarmi');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventStatusFilter]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadEvents(eventStatusFilter).catch((err) => {
        console.error('Errore refresh automatico allarmi:', err);
      });
    }, EVENTS_REFRESH_MS);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventStatusFilter]);

  const stats = useMemo(() => {
    const enabledRules = rules.filter((rule) => rule.enabled).length;
    const pendingRules = rules.filter((rule) => rule.current_state === 'pending').length;
    const alarmRules = rules.filter((rule) => rule.current_state === 'alarm').length;
    const activeEvents = events.filter((event) => event.status === 'active').length;
    const voiceScheduled = events.filter((event) => ['scheduled', 'pending'].includes(normalizeVoiceStatus(event.voice_call_status))).length;
    const voiceInProgress = events.filter((event) => ['initiated', 'ringing', 'answered'].includes(normalizeVoiceStatus(event.voice_call_status))).length;
    const voiceAcknowledged = events.filter((event) => event.voice_acknowledged === true).length;

    return {
      enabledRules,
      pendingRules,
      alarmRules,
      activeEvents,
      voiceScheduled,
      voiceInProgress,
      voiceAcknowledged,
    };
  }, [rules, events]);

  const filteredEvents = useMemo(() => {
    return events.filter((event) => eventMatchesVoiceFilter(event, voiceStatusFilter));
  }, [events, voiceStatusFilter]);

  const filteredRules = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return rules;

    return rules.filter((rule) => {
      const hay = [
        rule.device_id,
        rule.sensor_id,
        rule.sensor_label,
        rule.cell_type,
        getRefLabel(rule.machineRef),
        getRefLabel(rule.departmentRef),
        getCustomerLabel(rule.customerUserId),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rules, query]);

  const updateRuleForm = (ruleId, field, value) => {
    setForms((prev) => ({
      ...prev,
      [ruleId]: {
        ...(prev[ruleId] || {}),
        [field]: value,
      },
    }));
  };

  const saveRule = async (rule) => {
    const form = forms[rule._id] || {};

    const minRaw = String(form.min_value ?? '').trim();
    const maxRaw = String(form.max_value ?? '').trim();
    const delayRaw = String(form.delay_minutes ?? '5').trim();

    const minValue = minRaw === '' ? null : Number(minRaw);
    const maxValue = maxRaw === '' ? null : Number(maxRaw);
    const delayMinutes = Number(delayRaw || 5);

    if (minValue !== null && !Number.isFinite(minValue)) {
      setError('La soglia minima non è valida.');
      return;
    }

    if (maxValue !== null && !Number.isFinite(maxValue)) {
      setError('La soglia massima non è valida.');
      return;
    }

    if (minValue !== null && maxValue !== null && minValue >= maxValue) {
      setError('La soglia minima deve essere inferiore alla soglia massima.');
      return;
    }

    if (!Number.isFinite(delayMinutes) || delayMinutes < 1) {
      setError('Il ritardo deve essere almeno 1 minuto.');
      return;
    }

    if (form.enabled && minValue === null && maxValue === null) {
      setError('Per attivare una soglia devi impostare almeno minimo o massimo.');
      return;
    }

    try {
      setSavingRuleId(rule._id);
      setError('');
      setMessage('');

      const res = await authFetch(`/api/alarms/rules/${rule._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          min_value: minValue,
          max_value: maxValue,
          delay_minutes: delayMinutes,
          enabled: Boolean(form.enabled),
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore aggiornamento soglia');
      }

      setMessage(
        Boolean(form.enabled)
          ? 'Soglia salvata e monitoraggio attivo.'
          : 'Soglia salvata, monitoraggio disattivato.'
      );
      await loadRules();
    } catch (err) {
      setError(err?.message || 'Impossibile salvare la soglia.');
    } finally {
      setSavingRuleId(null);
    }
  };

  const ackEvent = async (event) => {
    try {
      setActionEventId(event._id);
      setError('');
      setMessage('');

      const res = await authFetch(`/api/alarms/events/${event._id}/ack`, {
        method: 'POST',
      });

      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore presa in carico allarme');
      }

      setMessage('Allarme preso in carico.');
      await Promise.all([
        loadRules(),
        loadEvents(eventStatusFilter),
      ]);
    } catch (err) {
      setError(err?.message || 'Impossibile prendere in carico l’allarme.');
    } finally {
      setActionEventId(null);
    }
  };

  const resolveEvent = async (event) => {
    try {
      setActionEventId(event._id);
      setError('');
      setMessage('');

      const res = await authFetch(`/api/alarms/events/${event._id}/resolve`, {
        method: 'POST',
      });

      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore risoluzione allarme');
      }

      setMessage('Allarme risolto.');
      await Promise.all([
        loadRules(),
        loadEvents(eventStatusFilter),
      ]);
    } catch (err) {
      setError(err?.message || 'Impossibile risolvere l’allarme.');
    } finally {
      setActionEventId(null);
    }
  };


  const testVoiceCall = async (event) => {
    try {
      setVoiceActionEventId(event._id);
      setError('');
      setMessage('');

      const res = await authFetch(`/api/voice/test-call/${event._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceNewToken: true }),
      });

      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore avvio chiamata test');
      }

      setMessage(
        data?.callSid
          ? `Chiamata test avviata verso ${data?.to || 'numero configurato'}. SID: ${data.callSid}`
          : `Chiamata test avviata verso ${data?.to || 'numero configurato'}.`
      );

      await loadEvents(eventStatusFilter);
    } catch (err) {
      setError(err?.message || 'Impossibile avviare la chiamata test.');
    } finally {
      setVoiceActionEventId(null);
    }
  };


  const retryVoiceCall = async (event) => {
  try {
    setVoiceActionEventId(event._id);
    setError('');
    setMessage('');

    const res = await authFetch(`/api/alarms/events/${event._id}/voice/retry`, {
      method: 'POST',
    });

    const data = await res.json().catch(() => null);

    if (!res || !res.ok) {
      throw new Error(data?.message || 'Errore richiesta richiamata');
    }

    setMessage(data?.message || 'Richiamata richiesta.');
    await loadEvents(eventStatusFilter);
  } catch (err) {
    setError(err?.message || 'Impossibile richiamare.');
  } finally {
    setVoiceActionEventId(null);
  }
};

const resetVoiceState = async (event) => {
  const confirmed = window.confirm(
    'Vuoi davvero resettare lo stato della chiamata vocale per questo allarme? Operazione utile solo per test o chiamate bloccate.'
  );

  if (!confirmed) return;

  try {
    setVoiceActionEventId(event._id);
    setError('');
    setMessage('');

    const res = await authFetch(`/api/alarms/events/${event._id}/voice/reset`, {
      method: 'POST',
    });

    const data = await res.json().catch(() => null);

    if (!res || !res.ok) {
      throw new Error(data?.message || 'Errore reset stato voce');
    }

    setMessage(data?.message || 'Stato voce resettato.');
    await loadEvents(eventStatusFilter);
  } catch (err) {
    setError(err?.message || 'Impossibile resettare la voce.');
  } finally {
    setVoiceActionEventId(null);
  }
};

const manualAckVoice = async (event) => {
  const confirmed = window.confirm(
    'Confermi la presa in carico manuale dell’allarme vocale?'
  );

  if (!confirmed) return;

  try {
    setVoiceActionEventId(event._id);
    setError('');
    setMessage('');

    const res = await authFetch(`/api/alarms/events/${event._id}/voice/manual-ack`, {
      method: 'POST',
    });

    const data = await res.json().catch(() => null);

    if (!res || !res.ok) {
      throw new Error(data?.message || 'Errore ACK manuale');
    }

    setMessage(data?.message || 'ACK manuale registrato.');
    await Promise.all([
      loadRules(),
      loadEvents(eventStatusFilter),
    ]);
  } catch (err) {
    setError(err?.message || 'Impossibile registrare ACK manuale.');
  } finally {
    setVoiceActionEventId(null);
  }
};


  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-lg animate-pulse">
          Caricamento allarmi e soglie...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-gray-100 to-white pb-10">
      <div className="max-w-7xl mx-auto px-4 pt-8 md:pt-10 space-y-6">
        <div className="bg-white rounded-3xl shadow-sm p-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
                Monitoraggio temperature
              </p>

              <h1 className="mt-2 text-3xl font-bold text-gray-900">
                Allarmi e soglie
              </h1>

              <p className="text-sm text-gray-500 mt-2 max-w-3xl leading-6">
                Configura le soglie per ogni sonda associata. Gli allarmi partono
                solo dopo il ritardo impostato, così eviti falsi positivi durante
                assestamenti o aperture temporanee.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={loadAll}
                className="px-4 py-2 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 text-sm font-semibold transition"
              >
                Aggiorna
              </button>

              <button
                type="button"
                onClick={() => navigate('/dispositivi-smart')}
                className="px-4 py-2 bg-slate-100 text-slate-800 rounded-xl hover:bg-slate-200 text-sm font-semibold transition"
              >
                Dispositivi smart
              </button>

              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-900 text-sm font-semibold transition"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm shadow-sm">
            {error}
          </div>
        )}

        {message && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm shadow-sm">
            {message}
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Soglie attive
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-900">
              {stats.enabledRules}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Canali attualmente monitorati.
            </p>
          </Card>

          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
              Fuori soglia
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-900">
              {stats.pendingRules}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              In attesa del ritardo impostato.
            </p>
          </Card>

          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
              Canali in allarme
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-900">
              {stats.alarmRules}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Soglie violate oltre il ritardo.
            </p>
          </Card>

          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
              Eventi attivi
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-900">
              {stats.activeEvents}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Allarmi ancora aperti.
            </p>
          </Card>


          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
              ACK telefonici
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-900">
              {stats.voiceAcknowledged}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Chiamate confermate con tasto 1.
            </p>
          </Card>
        </section>

        <section className="bg-white rounded-3xl shadow-sm p-5 md:p-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Soglie sonde
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Le righe vengono create automaticamente quando completi
                l’associazione del device.
              </p>
            </div>

            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca cliente, reparto, macchina, device..."
              className="w-full lg:w-[380px] border border-gray-300 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500 bg-white text-sm"
            />
          </div>

          <div className="mt-5">
            {filteredRules.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
                <p className="text-sm text-gray-500">
                  Nessuna soglia disponibile. Completa prima l’associazione di un dispositivo smart.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-3 text-left">Stato</th>
                      <th className="px-4 py-3 text-left">Cliente</th>
                      <th className="px-4 py-3 text-left">Reparto</th>
                      <th className="px-4 py-3 text-left">Macchina</th>
                      <th className="px-4 py-3 text-left">Device / Sonda</th>
                      <th className="px-4 py-3 text-left">Ultimo valore</th>
                      <th className="px-4 py-3 text-left">Min</th>
                      <th className="px-4 py-3 text-left">Max</th>
                      <th className="px-4 py-3 text-left">Ritardo</th>
                      <th className="px-4 py-3 text-left">Attiva</th>
                      <th className="px-4 py-3 text-right">Azione</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRules.map((rule) => {
                      const form = forms[rule._id] || {};
                      const badge = getRuleStateBadge(rule);

                      return (
                        <tr key={rule._id} className="border-t border-gray-100 hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.className}`}>
                              {badge.label}
                            </span>
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            {getCustomerLabel(rule.customerUserId)}
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            {getRefLabel(rule.departmentRef)}
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            <div className="font-semibold text-gray-800">
                              {getRefLabel(rule.machineRef)}
                            </div>
                            {rule.cell_type && (
                              <div className="mt-1 text-xs text-gray-500">
                                {rule.cell_type}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            <div className="font-semibold text-gray-800">
                              {rule.device_id}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {rule.sensor_label || rule.sensor_id} · {rule.sensor_id}
                            </div>
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            <div className="font-semibold">
                              {formatValue(rule.last_value)} {rule.unit || 'C'}
                            </div>
                            <div className="mt-1 text-xs text-gray-400">
                              {formatDateTime(rule.last_seen_at)}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <input
                              type="number"
                              step="0.1"
                              value={form.min_value ?? ''}
                              onChange={(e) =>
                                updateRuleForm(rule._id, 'min_value', e.target.value)
                              }
                              placeholder="Min"
                              className="w-[90px] border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                            />
                          </td>

                          <td className="px-4 py-3">
                            <input
                              type="number"
                              step="0.1"
                              value={form.max_value ?? ''}
                              onChange={(e) =>
                                updateRuleForm(rule._id, 'max_value', e.target.value)
                              }
                              placeholder="Max"
                              className="w-[90px] border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                            />
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={form.delay_minutes ?? '5'}
                                onChange={(e) =>
                                  updateRuleForm(rule._id, 'delay_minutes', e.target.value)
                                }
                                className="w-[80px] border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                              />
                              <span className="text-xs text-gray-500">min</span>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={Boolean(form.enabled)}
                                onChange={(e) =>
                                  updateRuleForm(rule._id, 'enabled', e.target.checked)
                                }
                              />
                              Sì
                            </label>
                          </td>

                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              disabled={savingRuleId === rule._id}
                              onClick={() => saveRule(rule)}
                              className="px-3 py-2 rounded-lg bg-cyan-600 text-white text-xs font-semibold hover:bg-cyan-700 transition disabled:opacity-60"
                            >
                              {savingRuleId === rule._id ? 'Salvo...' : 'Salva'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-3xl shadow-sm p-5 md:p-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Eventi allarme
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Storico degli allarmi generati dal controllo soglie, con stato chiamate automatiche e ACK telefonico.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setEventStatusFilter(filter.value)}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold transition ${
                    eventStatusFilter === filter.value
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 rounded-2xl border border-slate-100 bg-slate-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Filtro chiamate automatiche
              </p>
              <p className="text-xs text-slate-400">
                Isola allarmi da chiamare, in corso, confermati o problematici.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {VOICE_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setVoiceStatusFilter(filter.value)}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold transition ${
                    voiceStatusFilter === filter.value
                      ? 'bg-indigo-700 text-white'
                      : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            {filteredEvents.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
                <p className="text-sm text-gray-500">
                  Nessun evento allarme per i filtri selezionati.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {filteredEvents.map((event) => {
                  const badge = getEventBadge(event.status);

                  return (
                    <div
                      key={event._id}
                      className={`rounded-2xl border p-4 shadow-sm ${
                        event.status === 'active'
                          ? 'border-red-200 bg-red-50'
                          : event.status === 'acknowledged'
                          ? 'border-amber-200 bg-amber-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-bold text-slate-900">
                            {getEventTypeLabel(event.type)}
                          </h3>

                          <p className="mt-1 text-sm text-slate-600">
                            {getRefLabel(event.machineRef)} · {event.sensor_label || event.sensor_id}
                          </p>
                        </div>

                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.className}`}>
                          {badge.label}
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-xl bg-white/70 border border-white px-3 py-2">
                          <p className="text-xs text-slate-400">Valore</p>
                          <p className="font-bold text-slate-900">
                            {formatValue(event.value)} {event.unit || 'C'}
                          </p>
                        </div>

                        <div className="rounded-xl bg-white/70 border border-white px-3 py-2">
                          <p className="text-xs text-slate-400">Range</p>
                          <p className="font-bold text-slate-900">
                            {formatValue(event.min_value)} / {formatValue(event.max_value)}
                          </p>
                        </div>

                        <div className="rounded-xl bg-white/70 border border-white px-3 py-2">
                          <p className="text-xs text-slate-400">Reparto</p>
                          <p className="font-semibold text-slate-800">
                            {getRefLabel(event.departmentRef)}
                          </p>
                        </div>

                        <div className="rounded-xl bg-white/70 border border-white px-3 py-2">
                          <p className="text-xs text-slate-400">Cliente</p>
                          <p className="font-semibold text-slate-800">
                            {getCustomerLabel(event.customerUserId)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 text-xs text-slate-500 space-y-1">
                        <p>
                          Fuori soglia da: <strong>{formatDateTime(event.first_out_of_range_at)}</strong>
                        </p>
                        <p>
                          Allarme creato: <strong>{formatDateTime(event.triggered_at)}</strong>
                        </p>
                        {event.resolved_at && (
                          <p>
                            Risolto: <strong>{formatDateTime(event.resolved_at)}</strong>
                          </p>
                        )}
                      </div>

                      {(() => {
                        const voiceBadge = getVoiceBadge(event);
                        const attemptCount = getVoiceAttemptCount(event);
                        const lastAttempt = getVoiceLastAttempt(event);
                        const displayedPhone =
                          lastAttempt?.phone ||
                          event.voice_call_to ||
                          event.notifications?.call?.recipients?.slice?.(-1)?.[0] ||
                          '';
                        const attemptsTimeline = getVoiceAttemptsTimeline(event).slice(0, 3);

                        const hasDifferentStoredPhone =
                          Boolean(lastAttempt?.phone) &&
                          Boolean(event.voice_call_to) &&
                          lastAttempt.phone !== event.voice_call_to;

                        return (
                          <div className="mt-4 rounded-2xl border border-slate-200 bg-white/80 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Chiamata automatica
                                </p>
                                <p className="mt-1 text-sm font-bold text-slate-900">
                                  {displayedPhone || 'Numero non impostato'}
                                </p>

                                {hasDifferentStoredPhone && (
                                  <p className="mt-1 text-[11px] text-amber-600">
                                    Nota: valore storico salvato sull’evento: {event.voice_call_to}
                                  </p>
                                )}
                              </div>

                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${voiceBadge.className}`}>
                                {voiceBadge.label}
                              </span>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                              <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                                <p className="text-slate-400">Tentativi</p>
                                <p className="font-bold text-slate-800">
                                  {attemptCount}
                                </p>
                              </div>

                              <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                                <p className="text-slate-400">Ultimo stato</p>
                                <p className="font-bold text-slate-800">
                                  {getVoiceStatusLabel(event.voice_call_status)}
                                </p>
                              </div>

                              <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                                <p className="text-slate-400">ACK da</p>
                                <p className="font-bold text-slate-800">
                                  {event.voice_ack_by_phone || '-'}
                                </p>
                              </div>

                              <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                                <p className="text-slate-400">Ora ACK</p>
                                <p className="font-bold text-slate-800">
                                  {formatDateTime(event.voice_ack_at)}
                                </p>
                              </div>
                            </div>

                            {normalizeVoiceStatus(event.voice_escalation_status).includes('rate_limit') && (
                              <p className="mt-3 rounded-xl bg-orange-50 border border-orange-100 px-3 py-2 text-xs text-orange-700">
                                Chiamata temporaneamente bloccata dall’anti-spam. Il sistema riproverà automaticamente.
                              </p>
                            )}
                            
                            {event.voice_last_error && (
                              <p className="mt-3 rounded-xl bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
                                {event.voice_last_error}
                              </p>
                            )}
                            {attemptsTimeline.length > 0 && (
                              <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Ultimi tentativi
                                </p>

                                <div className="mt-2 space-y-2">
                                  {attemptsTimeline.map((attempt, index) => (
                                    <div
                                      key={attempt._id || attempt.call_sid || index}
                                      className="rounded-lg bg-white border border-slate-100 px-3 py-2 text-xs"
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="font-semibold text-slate-800">
                                          {attempt.phone || 'Numero non disponibile'}
                                        </span>
                                        <span className="text-slate-500">
                                          {getVoiceStatusLabel(attempt.status)}
                                        </span>
                                      </div>

                                      <div className="mt-1 text-slate-400">
                                        Avvio: {formatDateTime(attempt.started_at)}
                                      </div>

                                      {attempt.acknowledged && (
                                        <div className="mt-1 font-semibold text-emerald-700">
                                          Confermato: {formatDateTime(attempt.ack_at)}
                                        </div>
                                      )}

                                      {attempt.error && (
                                        <div className="mt-1 text-red-600">
                                          {attempt.error}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={voiceActionEventId === event._id || !canRetryVoiceCall(event)}
                                onClick={() => retryVoiceCall(event)}
                                className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
                              >
                                Richiama
                              </button>

                              <button
                                type="button"
                                disabled={voiceActionEventId === event._id || !canManualAckVoice(event)}
                                onClick={() => manualAckVoice(event)}
                                className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
                              >
                                ACK manuale
                              </button>

                              <button
                                type="button"
                                disabled={voiceActionEventId === event._id || !canResetVoiceState(event)}
                                onClick={() => resetVoiceState(event)}
                                className="px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-100 transition disabled:opacity-50"
                              >
                                Reset voce
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      {event.status !== 'resolved' && (
                        <div className="mt-4 flex gap-2">
                          {event.status === 'active' && (
                            <button
                              type="button"
                              disabled={actionEventId === event._id}
                              onClick={() => ackEvent(event)}
                              className="flex-1 px-3 py-2 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition disabled:opacity-60"
                            >
                              Prendi in carico
                            </button>
                          )}

                          <button
                            type="button"
                            disabled={actionEventId === event._id}
                            onClick={() => resolveEvent(event)}
                            className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition disabled:opacity-60"
                          >
                            Risolvi
                          </button>

                          <button
                            type="button"
                            disabled={voiceActionEventId === event._id}
                            onClick={() => testVoiceCall(event)}
                            className="flex-1 px-3 py-2 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 transition disabled:opacity-60"
                          >
                            {voiceActionEventId === event._id ? 'Chiamo...' : 'Test chiamata'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}